// src/pages/Publica/Pago.js
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../../config";
import { CHECKOUT_CONSENT_SESSION_KEY, CHECKOUT_OWNED_NOTICE_KEY, clearSessionKeys } from "../../utils/sessionKeys";
import ConsentCheckbox from "../../components/ConsentCheckbox";
import { TERMS_VERSION, PRIVACY_VERSION } from "../../utils/consentVersions";
import useCheckoutTexts from "../../hooks/useCheckoutTexts";
import usePublicidadCarrito from "../../hooks/usePublicidadCarrito";
import { loadRegistrationFormDraft, saveRegistrationFormDraft } from "../../utils/registrationFormDraft";
import {
  backupCheckoutCartCourses,
  clearCheckoutCartBackup,
  reconcileCartWithPublicCourses,
  mergeRegistrationCoursesWithCart,
  readCartCourses,
  readCartCourseIds,
  restoreCheckoutCartBackup,
} from "../../utils/cartCourseSelection";
import { getCourseCheckoutPrice } from "../../utils/coursePrice";
import { useOwnedCourseIds } from "../../hooks/useOwnedCourseIds";
import { COURSE_ALREADY_OWNED_CODE, isCourseOwnedByUser } from "../../utils/ownedCourseIds";
import { checkCartOwnershipWithBackend } from "../../utils/checkCartOwnership";
import { getMessage } from "../../data/locales";
import "./Pago.css";
import CourseImageFill from "../../components/CourseImageFill";

const CONSENT_ERROR_MESSAGE = "Debes aceptar los términos y condiciones y la política de privacidad.";
const INVALID_COUPON_MESSAGE = "Este código no es válido, por favor, introduce uno que sea válido.";
const CHECKOUT_COUPON_SESSION_KEY = "checkout_coupon_code";
/** Tax display uses DEFAULT_TAX until dynamic tax requirements are defined. */
const DEFAULT_TAX = 0;

const Pago = () => {
  const navigate = useNavigate();
  const [carrito, setCarrito] = useState([]);
  const [total, setTotal] = useState(0);

  const [procesando, setProcesando] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [cuponCodigo, setCuponCodigo] = useState(() => {
    try {
      return sessionStorage.getItem(CHECKOUT_COUPON_SESSION_KEY) || "";
    } catch {
      return "";
    }
  });
  const [cuponError, setCuponError] = useState("");
  const [consentAccepted, setConsentAccepted] = useState(() => {
    try {
      return sessionStorage.getItem(CHECKOUT_CONSENT_SESSION_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [consentError, setConsentError] = useState("");
  const [ownedCourseError, setOwnedCourseError] = useState("");
  const { ownedCourseIds, ownershipReady } = useOwnedCourseIds();
  const { texts: checkoutTexts, loading: checkoutTextsLoading } = useCheckoutTexts();
  const { publicidad: publicidadCarrito } = usePublicidadCarrito();

  const handleConsentChange = (accepted) => {
    setConsentAccepted(accepted);
    try {
      if (accepted) {
        sessionStorage.setItem(CHECKOUT_CONSENT_SESSION_KEY, "1");
      } else {
        sessionStorage.removeItem(CHECKOUT_CONSENT_SESSION_KEY);
      }
    } catch {
      /* ignore storage failures */
    }
    if (accepted) setConsentError("");
  };

  const handleCouponChange = (value) => {
    setCuponCodigo(value);
    setCuponError("");
    try {
      const normalizedValue = value.trim();
      if (normalizedValue) {
        sessionStorage.setItem(CHECKOUT_COUPON_SESSION_KEY, normalizedValue);
      } else {
        sessionStorage.removeItem(CHECKOUT_COUPON_SESSION_KEY);
      }
    } catch {
      /* ignore storage failures */
    }
  };

  // Ayudante defensivo para sumar con seguridad
// Envolvemos en useCallback para que React sepa que la función no cambia
const calcularTotal = useCallback((items) => {
  return items.reduce((acc, curso) => acc + getCourseCheckoutPrice(curso), 0);
}, []);

const refreshCartCourses = useCallback(async (items) => {
  const courseIds = items
    .map((item) => (item && typeof item._id === "string" ? item._id.trim() : ""))
    .filter(Boolean);

  if (courseIds.length === 0) {
    return;
  }

  const responses = await Promise.allSettled(
    courseIds.map(async (courseId) => {
      const response = await fetch(`${API_URL}/cursos/${courseId}`);
      if (response.status === 404) {
        return { courseId, status: "unavailable" };
      }
      if (!response.ok) {
        throw new Error(`No se pudo verificar el curso ${courseId}`);
      }
      return { courseId, status: "verified", course: await response.json() };
    }),
  );

  const verifiedResults = responses
    .filter((result) => result.status === "fulfilled" && result.value)
    .map((result) => result.value);
  if (verifiedResults.length === 0) {
    return;
  }
  const currentCourses = verifiedResults
    .filter((result) => result.status === "verified" && result.course)
    .map((result) => result.course);
  const verifiedCourseIds = verifiedResults
    .filter((result) => result.status === "verified")
    .map((result) => result.courseId);
  const unavailableCourseIds = verifiedResults
    .filter((result) => result.status === "unavailable")
    .map((result) => result.courseId);

  const latestStoredCart = readCartCourses();
  const refreshedCart = reconcileCartWithPublicCourses(latestStoredCart, currentCourses, {
    preserveUnverified: true,
    verifiedCourseIds,
    unavailableCourseIds,
  });
  const nextCart = ownedCourseIds.length > 0
    ? refreshedCart.filter((curso) => !isCourseOwnedByUser(curso._id, ownedCourseIds))
    : refreshedCart;
  if (nextCart.length !== refreshedCart.length) {
    setOwnedCourseError(
      nextCart.length === 0
        ? getMessage("checkout.alreadyOwnedCourse")
        : getMessage("checkout.alreadyOwnedCoursesInCart"),
    );
  }

  localStorage.setItem("carrito", JSON.stringify(nextCart));
  setCarrito(nextCart);
  setTotal(calcularTotal(nextCart));
}, [calcularTotal, ownedCourseIds]);

const cargarCarrito = useCallback(() => {
  let ownedNotice = "";
  try {
    ownedNotice = sessionStorage.getItem(CHECKOUT_OWNED_NOTICE_KEY) || "";
    if (ownedNotice === "all") {
      setOwnedCourseError(getMessage("checkout.alreadyOwnedCourse"));
    } else if (ownedNotice === "some") {
      setOwnedCourseError(getMessage("checkout.alreadyOwnedCoursesInCart"));
    }
    if (ownedNotice) {
      sessionStorage.removeItem(CHECKOUT_OWNED_NOTICE_KEY);
    }
  } catch {
    ownedNotice = "";
  }
  const storedCart = readCartCourses();
  const carritoGuardado = storedCart.length > 0
    ? storedCart
    : (ownedNotice === "all" ? [] : restoreCheckoutCartBackup());
  setCarrito(carritoGuardado);
  setTotal(calcularTotal(carritoGuardado));
  refreshCartCourses(carritoGuardado).catch((error) => {
    console.error(error);
  });
}, [calcularTotal, refreshCartCourses]);

const eliminarCurso = (id) => {
  setCarrito((prevCarrito) => {
    const nuevoCarrito = prevCarrito.filter((curso) => curso._id !== id);
    localStorage.setItem("carrito", JSON.stringify(nuevoCarrito));
    setTotal(calcularTotal(nuevoCarrito));
    return nuevoCarrito;
  });
};

  useEffect(() => {
    const controller = new AbortController();
    fetch(`${API_URL}/verificar-token`, { credentials: "include", signal: controller.signal })
      .then(res => setIsAuthenticated(res.ok))
      .catch((err) => {
        if (err.name !== "AbortError") setIsAuthenticated(false);
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    cargarCarrito();
    globalThis.addEventListener("storage", cargarCarrito);
    return () => globalThis.removeEventListener("storage", cargarCarrito);
  }, [cargarCarrito]);

  useEffect(() => {
    if (!ownershipReady || ownedCourseIds.length === 0) {
      return;
    }
    setCarrito((prevCarrito) => {
      const kept = prevCarrito.filter((curso) => !isCourseOwnedByUser(curso._id, ownedCourseIds));
      if (kept.length === prevCarrito.length) {
        return prevCarrito;
      }
      localStorage.setItem("carrito", JSON.stringify(kept));
      setTotal(calcularTotal(kept));
      setOwnedCourseError(
        kept.length === 0
          ? getMessage("checkout.alreadyOwnedCourse")
          : getMessage("checkout.alreadyOwnedCoursesInCart"),
      );
      return kept;
    });
  }, [ownedCourseIds, ownershipReady, calcularTotal]);

  useEffect(() => {
    if (!isAuthenticated || carrito.length === 0) {
      return;
    }
    let cancelled = false;
    checkCartOwnershipWithBackend(carrito.map((curso) => curso._id))
      .then((result) => {
        if (cancelled || !result.blocked) {
          return;
        }
        const kept = carrito.filter((curso) => !isCourseOwnedByUser(curso._id, result.courseIds));
        const keptIds = kept.map((curso) => curso._id).join(",");
        const currentIds = carrito.map((curso) => curso._id).join(",");
        setOwnedCourseError(
          kept.length === 0
            ? getMessage("checkout.alreadyOwnedCourse")
            : getMessage("checkout.alreadyOwnedCoursesInCart"),
        );
        if (keptIds === currentIds) {
          return;
        }
        localStorage.setItem("carrito", JSON.stringify(kept));
        setCarrito(kept);
        setTotal(calcularTotal(kept));
      })
      .catch(() => {
        /* POST /api/pedidos still blocks a duplicate purchase */
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, carrito, calcularTotal]);


  const navigateToRegistration = () => {
    const cartCourseIds = readCartCourseIds();
    if (cartCourseIds.length > 0) {
      const draft = loadRegistrationFormDraft();
      saveRegistrationFormDraft({
        ...draft,
        cuponCodigo: cuponCodigo.trim(),
        cursos: mergeRegistrationCoursesWithCart(
          draft.cursos,
          cartCourseIds,
          carrito.map((item) => item._id),
        ),
      });
    }
    navigate("/NewRegistro?redirect=/pago");
  };

  const manejarPago = async () => {
    if (carrito.length === 0) return;
    if (!ownershipReady) return;
    if (carrito.some((curso) => isCourseOwnedByUser(curso._id, ownedCourseIds))) {
      setOwnedCourseError(getMessage("checkout.alreadyOwnedCoursesInCart"));
      return;
    }
    
    // Doble validación por si acaso
    if (!isAuthenticated) {
      alert("Por favor, inicia sesión para continuar.");
      return;
    }

    if (!consentAccepted) {
      setConsentError(CONSENT_ERROR_MESSAGE);
      return;
    }
    setCuponError("");

    setProcesando(true);

    try {
      const items = carrito.map((c) => ({ cursoId: c._id, qty: 1 }));

      const res = await fetch(`${API_URL}/api/pedidos`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          items,
          consentAccepted,
          termsVersion: TERMS_VERSION,
          policyVersion: PRIVACY_VERSION,
          cuponCodigo: cuponCodigo.trim(),
        }),
      });

      if (res.status === 404 || res.status === 409) {
        const conflictData = await res.json().catch(() => ({}));
        if (conflictData?.code === COURSE_ALREADY_OWNED_CODE) {
          const ownedIds = Array.isArray(conflictData.courseIds)
            ? conflictData.courseIds.map(String)
            : ownedCourseIds;
          const kept = carrito.filter((curso) => !isCourseOwnedByUser(curso._id, ownedIds));
          localStorage.setItem("carrito", JSON.stringify(kept));
          setCarrito(kept);
          setTotal(calcularTotal(kept));
          setOwnedCourseError(
            kept.length === 0
              ? getMessage("checkout.alreadyOwnedOrderBlocked")
              : getMessage("checkout.alreadyOwnedCoursesInCart"),
          );
          return;
        }
        localStorage.removeItem("carrito");
        clearCheckoutCartBackup();
        setCarrito([]);
        setTotal(0);
        alert("⚠️ Algunos cursos ya no están disponibles. El carrito se ha vaciado.");
        return;
      }

      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          alert("🔒 Tu sesión ha expirado. Por favor, inicia sesión de nuevo.");
          // Guardamos el carrito temporalmente
          const carritoGuardado = localStorage.getItem("carrito");
          
          clearSessionKeys();
          
          // Restauramos el carrito
          if (carritoGuardado) {
              localStorage.setItem("carrito", carritoGuardado);
          }
          
          setIsAuthenticated(false);
          setConsentAccepted(false);
          setConsentError("");
          setProcesando(false);
          return;
      }
        const errorData = await res.json();
        if (errorData?.code === "COURSE_NOT_PURCHASABLE") {
          await refreshCartCourses(carrito);
          alert("⚠️ Algunos cursos ya no están disponibles para compra y se han retirado del carrito.");
          return;
        }
        const errorMessage = errorData.error || "Error al crear el pedido";
        if (errorMessage === INVALID_COUPON_MESSAGE) {
          setCuponError(errorMessage);
          return;
        }
        throw new Error(errorMessage);
      }

      const data = await res.json();
      backupCheckoutCartCourses(carrito);
      sessionStorage.removeItem(CHECKOUT_CONSENT_SESSION_KEY);
      sessionStorage.removeItem(CHECKOUT_COUPON_SESSION_KEY);
      navigate(`/checkout?orderId=${data.orderId}`);

    } catch (error) {
      alert("❌ Error: " + error.message);
    } finally {
      setProcesando(false);
    }
  };

  if (carrito.length === 0) {
    return (
      <div className="pagina-pago pagina-pago--empty">
        <div className="carrito-vacio">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" /></svg>
          <h2>{checkoutTexts.tituloCarritoVacio}</h2>
          <p>{ownedCourseError || checkoutTexts.descripcionCarritoVacio}</p>
          {ownedCourseError ? (
            <button type="button" onClick={() => navigate("/usuario/campus")} className="btn-explorar">
              {getMessage("checkout.goToCampus")}
            </button>
          ) : (
            <button type="button" onClick={() => navigate("/cursos")} className="btn-explorar">
              Explorar Cursos
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="pagina-pago">
      <div className="pago-header">
        <h1>{checkoutTexts.tituloPagina}</h1>
        <p>{checkoutTexts.subtituloPagina}</p>
        {ownedCourseError ? (
          <p className="pago-owned-error" role="status">{ownedCourseError}</p>
        ) : null}
      </div>
      
      <div className="contenido-principal">
        <div className="seccion-carrito">
          <h2 className="titulo-seccion">
            Cursos seleccionados ({carrito.length})
          </h2>
          
          <div className="lista-carrito-premium">
            {carrito.map((curso) => (
              <div key={curso._id} className="item-carrito-premium">
                
                <div className="imagen-wrapper">
                  <CourseImageFill
                    src={curso.imagen || ""}
                    alt={curso.titulo}
                    imageClassName="imagen-curso-premium"
                  />
                </div>

                <div className="info-curso-premium">
                  <div className="info-cabecera">
                    <div className="cabecera-textos">
                      <h3 className="titulo-curso-premium">{curso.titulo}</h3>
                      <ul className="curso-meta-features">
                        {checkoutTexts.caracteristicas.map((feat) => (
                          <li key={feat}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> {feat}</li>
                        ))}
                      </ul>
                    </div>
                    
                    <button className="btn-eliminar-premium" onClick={() => eliminarCurso(curso._id)} aria-label="Eliminar curso">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      Eliminar
                    </button>
                  </div>

                  {curso.tags && curso.tags.length > 0 && (
                    <div className="tags-curso">
                      {curso.tags.map((tag, index) => (
                        // CORRECCIÓN: Uso de tag como key estable o combinación con index
                        <span key={`${tag}-${index}`} className="tag-badge">{tag}</span>
                      ))}
                    </div>
                  )}

                  <div className="info-pie">
                    <div className="precio-contenedor">
                      <span className="precio-valor">
                        {getCourseCheckoutPrice(curso).toFixed(2)} €
                      </span>
                      <span className="precio-label">Impuestos incluidos</span>
                    </div>
                  </div>
                </div>

              </div>
            ))}
          </div>
        </div>

        {/* --- NUEVA SECCIÓN DE AUTENTICACIÓN SIMPLIFICADA --- */}
        {!isAuthenticated && (
          <div className="auth-section">
            <div className="login-prompt">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" /></svg>
              <h3>Casi terminamos</h3>
              <p>Para procesar tu pedido de forma segura y darte acceso inmediato a los cursos, necesitas acceder a tu cuenta.</p>
              
              <div className="auth-actions">
                <button
                  className="btn-login-redirect"
                  onClick={() => {
                    backupCheckoutCartCourses(carrito);
                    navigate("/login?redirect=/pago");
                  }}
                >
                  Iniciar Sesión
                </button>
                <button className="btn-register-redirect" onClick={navigateToRegistration}>
                  Crear Cuenta Nueva
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="columna-lateral">
        <div className="resumen-pedido">
          <h3>Resumen del Pedido</h3>
          
          <div className="desglose-precio">
            <div className="fila-desglose">
              <span>Subtotal</span>
              <span>{total.toFixed(2)} €</span>
            </div>
            <div className="fila-desglose">
              <span>Impuestos</span>
              <span>{DEFAULT_TAX.toFixed(2)} €</span>
            </div>
            <div className="fila-desglose total-row">
              <span>Total</span>
              <span className="total-amount">{total.toFixed(2)} €</span>
            </div>
          </div>

          <label className="pago-cupon-field" htmlFor="pago-cupon-codigo">
            <span>Código de descuento</span>
            <input
              id="pago-cupon-codigo"
              type="text"
              value={cuponCodigo}
              onChange={(e) => handleCouponChange(e.target.value)}
              placeholder="Introduce tu código"
              className={`pago-cupon-input${cuponError ? " pago-cupon-input--error" : ""}`}
              aria-invalid={cuponError ? "true" : "false"}
              aria-describedby={cuponError ? "pago-cupon-error" : undefined}
            />
            {cuponError ? (
              <span id="pago-cupon-error" className="pago-cupon-error" role="alert">
                {cuponError}
              </span>
            ) : null}
          </label>

          <div className="consent-container">
             {isAuthenticated && (
              <ConsentCheckbox
                checked={consentAccepted}
                onChange={handleConsentChange}
                error={consentError}
              />
             )}
          </div>

          {isAuthenticated && (
            <button className="btn-pagar" onClick={manejarPago} disabled={procesando || !ownershipReady}>
              {procesando ? (
                <span className="loader">Procesando...</span>
              ) : !ownershipReady ? (
                getMessage("checkout.checkingAccess")
              ) : (
                <>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" /></svg>
                  Completar Pago Seguro
                </>
              )}
            </button>
          )}
          <p className="texto-seguro">{checkoutTexts.textoSeguridad}</p>
        </div>

        <div className="sidebar-info">
          <ul
            className="beneficios-lista"
            aria-busy={checkoutTextsLoading}
            aria-label={checkoutTextsLoading ? "Cargando beneficios" : "Beneficios"}
          >
            {checkoutTextsLoading
              ? Array.from({ length: 3 }, (_, index) => (
                  <li className="beneficio-skeleton" key={index} aria-hidden="true">
                    <span className="beneficio-skeleton-icono" />
                    <span className="beneficio-skeleton-texto">
                      <span className="beneficio-skeleton-linea beneficio-skeleton-titulo" />
                      <span className="beneficio-skeleton-linea beneficio-skeleton-descripcion" />
                    </span>
                  </li>
                ))
              : checkoutTexts.beneficios.map((b) => (
                  <li key={`${b.titulo}-${b.descripcion}`}>
                    <div className="icono"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" /></svg></div>
                    <div className="texto-beneficio">
                      <strong>{b.titulo}</strong>
                      <p>{b.descripcion}</p>
                    </div>
                  </li>
                ))}
          </ul>

          {publicidadCarrito && (
            <div className="pago-publicidad">
              <img src={publicidadCarrito} alt="Publicidad" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Pago;
